import * as z from "zod";
import { cursorPagination, decimal, flag, pagination, version } from "../index";
import { csScores, salesTeamScores } from "../party/enums";
import { MAX_LINE_UNITS } from "../til";
import {
  allocationBlockCauses,
  allocationExclusions,
  allocationReadinesses,
  allocationRunModes,
  allocationRunStatuses,
  allocationSkipReasons,
} from "./enums";
import { reason } from "./fields";
import { salesOrderDto } from "./sales-order";
import { salesOrderLineDto } from "./sales-order-line";

// Allocation (M7, 50-api/sales.md §2.6 and §2.7; spec v0.2 §6, FR-M7-01, 02, 06, 07; ADR-113, ADR-115): a run ranks
// the orders of a warehouse and offers them its stock; the Customer Service reads what it offered, confirms it in
// block, corrects it line by line and gives it back.

// --- Thresholds of the rules (`sales.allocation_parameter.thresholds`) ---

const rank = z.number().int().min(1).max(99);

/**
 * What each rule keeps in `thresholds`, by the code of its parameter. Rule 3 and rule 4 have their own table
 * (`sales.allocation_value_tier`), rule 1 has no threshold, and the incoterms of rule 5 carry their rank
 * (`reference.incoterm.allocation_rank`).
 */
export const allocationThresholds = {
  /** Rule 2: days to the end of the delivery window, ascending. Late is rank 1, then a rank per threshold. */
  R2_DELIVERY_WINDOW: z.object({ withinDays: z.array(z.number().int().min(0)).min(1).max(10) }),
  /** Rule 5: the rank of an order not on open terms (a term of Hilldun is, ADR-241), or whose incoterm has none. */
  R5_TERMS: z.object({ otherwise: rank }),
  /** Rule 6: the rank of each score of the sales team, and of a customer without one. */
  R6_SALES_SCORE: z.object({ ranks: z.record(z.enum(salesTeamScores), rank), unknown: rank }),
  /** Rule 7: the rank of each score of the Customer Service, and of a customer without one. */
  R7_CS_SCORE: z.object({ ranks: z.record(z.enum(csScores), rank), unknown: rank }),
};
export type AllocationThresholdCode = keyof typeof allocationThresholds;
/** The thresholds of every rule that has them, by the code of its parameter. */
export type AllocationThresholds = { [C in AllocationThresholdCode]: z.output<(typeof allocationThresholds)[C]> };

/**
 * The codes of `sales.allocation_parameter` (spec v0.2 §6), in the order of the default ranking (ADR-194): credit
 * first, the five rules Operations orders, then the larger order; rule 4 does not sort.
 */
export const allocationParameterCodes = [
  "R1_CREDIT",
  "R2_DELIVERY_WINDOW",
  "R3_ORDER_VALUE",
  "R5_TERMS",
  "R6_SALES_SCORE",
  "R7_CS_SCORE",
  "R3_5_VALUE_TIEBREAK",
  "R4_READINESS",
] as const;
export type AllocationParameterCode = (typeof allocationParameterCodes)[number];

// --- Process `allocation-run` (ADR-029): ids only ---

export const allocationRunParams = z.object({
  runId: z.uuid(),
  causedBy: z.uuid().nullable(),
  correlationId: z.string(),
});
export type AllocationRunParams = z.output<typeof allocationRunParams>;

/** Deterministic id of the instance: the request and its event start the same process. */
export const allocationRunInstance = (runId: string) => `allocation-run-${runId}`;

export const allocationRunStepInput = z.object({ runId: z.uuid() });
/** A step that offers stock names where it starts: the orders already offered, in the order of their ranking. */
export const allocationRunBlockInput = allocationRunStepInput.extend({ from: z.number().int().min(0) });

// --- Runs ---

/**
 * A run for a warehouse, with the id from the client (ADR-046): asked again with the same id it answers the run the
 * first request left.
 */
export const requestAllocationRunInput = z.object({ id: z.uuid(), locationId: z.uuid() });
export type RequestAllocationRunInput = z.input<typeof requestAllocationRunInput>;

export const getAllocationRunInput = z.object({ runId: z.uuid() });
export type GetAllocationRunInput = z.input<typeof getAllocationRunInput>;

/** Newest first. */
export const listAllocationRunsInput = z.object({
  location: z.uuid().optional(),
  status: z.enum(allocationRunStatuses).optional(),
  ...pagination({ defaultSize: 25, maxSize: 100 }),
});
export type ListAllocationRunsInput = z.input<typeof listAllocationRunsInput>;

/** What a run did, counted when it ended. */
export const allocationRunStatsDto = z.object({
  orders: z.number().int(),
  green: z.number().int(),
  yellow: z.number().int(),
  red: z.number().int(),
  /** Lines the run offered stock to, and how many units in all. */
  proposedLines: z.number().int(),
  proposedQty: z.number().int(),
  /** Lines offered less than they are open for. */
  blockedLines: z.number().int(),
  /**
   * ADR-193, ADR-240: units a night run took back before it ranked the orders, and how many of them passed to other
   * orders, from orders blocked for credit and from orders ranked after them. 0 for the runs that ended before.
   */
  releasedQty: z.number().int().default(0),
  fromCreditQty: z.number().int().default(0),
  fromRankQty: z.number().int().default(0),
  /** ADR-240: units a night run committed by itself; 0 for the other runs, which only offer. */
  committedQty: z.number().int().default(0),
});
export type AllocationRunStats = z.output<typeof allocationRunStatsDto>;

export const allocationRunDto = z
  .object({
    id: z.uuid(),
    locationId: z.uuid(),
    /** Code of the warehouse. */
    warehouse: z.string(),
    mode: z.enum(allocationRunModes),
    status: z.enum(allocationRunStatuses),
    /** Who asked for it; null for a user without a name, and for a run its schedule started. */
    requestedBy: z.string().nullable(),
    /** The time its schedule fell due (ADR-186); null for a run asked by hand. */
    scheduledFor: z.iso.datetime().nullable(),
    /** The orders it ranked; null until it did. */
    ordersConsidered: z.number().int().nullable(),
    /** How many of them were offered stock so far. */
    ordersProcessed: z.number().int(),
    /** ADR-240: how many of them a night run committed so far; 0 for the other runs. */
    ordersCommitted: z.number().int(),
    /** Open, and moved by nobody for 30 minutes: its process was lost, and the next request closes it (ADR-134). */
    stalled: z.boolean(),
    stats: allocationRunStatsDto.nullable(),
    error: z.string().nullable(),
    startedAt: z.iso.datetime().nullable(),
    finishedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
  })
  .meta({ id: "AllocationRun" });

// --- State of the allocation (spec v0.2 §9) ---

/** A page of the state of the allocation, and the orders one confirmation takes: the Customer Service confirms a page. */
export const ORDERS_PAGE_MAX = 200;

/** In the order the stock is offered: the ranked orders by their place, then the others by number. */
export const listAllocationOrdersInput = z.object({
  /** The orders a warehouse ships. */
  location: z.uuid().optional(),
  readiness: z.enum(allocationReadinesses).optional(),
  /** `true`: only the orders a run leaves out; `false`: only the others. */
  excluded: flag.optional(),
  customer: z.uuid().optional(),
  brand: z.uuid().optional(),
  ...pagination({ defaultSize: 50, maxSize: ORDERS_PAGE_MAX }),
});
export type ListAllocationOrdersInput = z.input<typeof listAllocationOrdersInput>;

/** The rank of an order on each rule that sorts: 1 is served first. */
export const allocationRanksDto = z.object({
  credit: z.number().int(),
  /**
   * Rule 1b (C-56, ADR-259): 1 for an order of an active season, 2 otherwise, written whether the rule sorts or not;
   * null for a ranking made before the rule.
   */
  activeSeason: z.number().int().nullable(),
  delivery: z.number().int(),
  value: z.number().int(),
  terms: z.number().int(),
  salesScore: z.number().int(),
  csScore: z.number().int(),
});

/**
 * An order not yet locked as the allocation sees it: why a run leaves it out, or where the latest run of its
 * warehouse ranked it and what it offered. Values are in the currency the thresholds are in (the order's, at its
 * exchange rate).
 */
export const allocationOrderDto = salesOrderDto
  .pick({
    soNumber: true,
    status: true,
    customerOrgId: true,
    customer: true,
    brand: true,
    warehouse: true,
    lotCount: true,
  })
  .extend({
    salesOrderId: z.uuid(),
    exclusion: z.enum(allocationExclusions).nullable(),
    /** The run that ranked it, and its place in it; null for an order no run of its warehouse has ranked. */
    runId: z.uuid().nullable(),
    position: z.number().int().nullable(),
    ranks: allocationRanksDto.nullable(),
    /** Days from the run to the end of the delivery window; negative when late, null without a date. */
    daysToDelivery: z.number().int().nullable(),
    /** Value of what is still to ship. */
    openValue: decimal.nullable().optional(),
    /** The share of it rule 4 asks for. */
    requiredValue: decimal.nullable().optional(),
    /** Value of what is allocated or offered. */
    bookedValue: decimal.nullable().optional(),
    readiness: z.enum(allocationReadinesses).nullable(),
    /** Units still to allocate, how many of them the run offered, and the ones already allocated. */
    qtyOpen: z.number().int(),
    qtyProposed: z.number().int(),
    qtyAllocated: z.number().int(),
    rankedAt: z.iso.datetime().nullable(),
  })
  .meta({ id: "AllocationOrder" });

/** A page of the lines of an order as the allocation sees them, and the lines one correction touches. */
const LINES_PAGE_MAX = 500;

/** By line number. */
export const listAllocationLinesInput = z.object({
  salesOrderId: z.uuid(),
  ...cursorPagination({ defaultLimit: 200, maxLimit: LINES_PAGE_MAX }),
});
export type ListAllocationLinesInput = z.input<typeof listAllocationLinesInput>;
export const allocationLinesQuery = listAllocationLinesInput.omit({ salesOrderId: true });

/**
 * A line of an order as the allocation sees it (FR-M7-01): what the warehouse of the order can allocate of its SKU,
 * what the latest run offered and, where it offered less, why (FR-M7-07).
 */
export const allocationLineDto = salesOrderLineDto
  .pick({
    id: true,
    lineNo: true,
    skuId: true,
    skuCode: true,
    styleCode: true,
    styleName: true,
    colour: true,
    size: true,
    qtyOrdered: true,
    qtyAllocated: true,
  })
  .extend({
    /** Still to allocate, and how much of it the latest run offered. */
    qtyOpen: z.number().int(),
    qtyProposed: z.number().int(),
    /**
     * ADR-178 §1.2, ADR-193, ADR-240: of what the latest run of the warehouse offered the line, or committed by itself at
     * night, the units it took back from other orders: they pass from those orders to this one, for the reason `credit`
     * (blocked for credit) or `rank` (ranked after this one).
     */
    qtyFromCredit: z.number().int(),
    qtyFromRank: z.number().int(),
    /** What the IMS says the warehouse can allocate of the SKU; null when it never said. */
    atp: z.number().int().nullable(),
    /** FR-M8-03: when the IMS said so. */
    atpUpdatedAt: z.iso.datetime().nullable(),
    /** What the IMS said is older than the parameter `allocation.atp_stale_minutes` (ADR-134). */
    atpStale: z.boolean(),
    blockCause: z.enum(allocationBlockCauses).nullable(),
  })
  .meta({ id: "AllocationLine" });

// --- Confirmation, correction and release (ADR-115) ---

/**
 * What a run offered to these orders becomes committed, checked against each order as it is now and against the
 * stock the warehouse has left: an order may get less than it was offered, or nothing, with the reason.
 */
export const confirmAllocationsInput = z.object({
  runId: z.uuid(),
  salesOrderIds: z
    .array(z.uuid())
    .min(1)
    .max(ORDERS_PAGE_MAX)
    .refine((ids) => new Set(ids).size === ids.length, { message: "Each order once" }),
});
export type ConfirmAllocationsInput = z.input<typeof confirmAllocationsInput>;
export const confirmAllocationsBody = confirmAllocationsInput.omit({ runId: true });

/** What a confirmation did to one order. */
export const allocationConfirmedOrderDto = z.object({
  salesOrderId: z.uuid(),
  soNumber: z.string().nullable(),
  /** Null when the order took what it could; otherwise why it was left as it is. */
  skipReason: z.enum(allocationSkipReasons).nullable(),
  /** Units committed now, and the units offered that it could not take. */
  qtyConfirmed: z.number().int(),
  qtyShort: z.number().int(),
  /** The version the order has after it: what the client sends next. */
  version: z.number().int().nullable(),
});

export const allocationConfirmationDto = z
  .object({ runId: z.uuid(), orders: z.array(allocationConfirmedOrderDto) })
  .meta({ id: "AllocationConfirmation" });

/**
 * FR-M7-02: the units each line has committed, said by a person, more or fewer than it has: a line without a
 * proposal is allocated by hand too. `version` is the one of the order the client read (409 otherwise).
 */
export const correctAllocationInput = z.object({
  salesOrderId: z.uuid(),
  version,
  lines: z
    .array(z.object({ lineId: z.uuid(), qtyAllocated: z.number().int().min(0).max(MAX_LINE_UNITS) }))
    .min(1)
    .max(LINES_PAGE_MAX),
  reason: reason.optional(),
});
export type CorrectAllocationInput = z.input<typeof correctAllocationInput>;
export const correctAllocationBody = correctAllocationInput.omit({ salesOrderId: true });

/** Everything an allocated order has committed is given back, with the reason. */
export const releaseAllocationInput = z.object({ salesOrderId: z.uuid(), version, reason });
export type ReleaseAllocationInput = z.input<typeof releaseAllocationInput>;
export const releaseAllocationBody = releaseAllocationInput.omit({ salesOrderId: true });
